import React, { useContext, useEffect, useMemo } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import { NecttosContext } from '../../context/NecttosContext';
import Button from '../../NecttosComp/Button/Button';
import { Table, Tbody, Td, Th, Thead, Tr, useSortableData } from '../../NecttosComp/Table/Table';
import { getMenuList } from '../../NecttosApp/windows/MenuList';
import { Switch } from 'antd';
import { icons } from '../../NecttosApp/windows/Icons';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import { useQuery } from 'react-query';
import { OLoading } from '../../static/svg/icons';
import Input2 from '../../NecttosComp/Input/Input2';
import Instruction from '../../Documentation/Instruction';

const getCustomFeatures = (collegeId) => async () => {
	const instance = await getAxiosTokenInstance();
	try {
		const resp = await instance.get('/settings/getCustomFeatures', {
			params: {
				collegeId,
				platform: 'web',
			},
		});

		if (resp.data.statusCode === 400) {
			throw new Error(resp.data.message || 'API Error');
		} else {
			return resp.data;
		}
	} catch (error) {
		throw new Error(error?.response?.data?.message || error.message || 'API Error');
	}
};

function FeatureManagement({ onClose }) {
	const { collegeId, college } = useContext(NecttosContext);
	const [neededMenuItems, setNeededMenuItems] = React.useState(['']);
	const [search, setSearch] = React.useState('');
	const [filters, setFilters] = React.useState({
		classification: 'all',
	});
	const [loading, setLoading] = React.useState(false);

	const MENU_ITEMS = useMemo(() => getMenuList(collegeId, college?.verificationWindows, college), [collegeId, college?.verificationWindows, college]);
	const {
		data: features,
		isFetching,
		refetch,
	} = useQuery(['getMenuFilterStatus'], getCustomFeatures(collegeId), {
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	useEffect(() => {
		if (features) {
			setNeededMenuItems(features?.list);
		}
	}, [features]);

	const classification = MENU_ITEMS.reduce((acc, item) => {
		if (!acc[item.classification]) {
			acc[item.classification] = [];
		}
		acc[item.classification].push(item);
		return acc;
	}, {});

	const handleSave = async () => {
		console.log('neededMenuItems', neededMenuItems);
		setLoading(true);
		const instance = await getAxiosTokenInstance();
		try {
			const resp = await instance.post('/settings/saveCustomFeatures', {
				collegeId: collegeId,
				features: neededMenuItems,
				platform: 'web',
			});
			if (resp.status === 400) {
				throw new Error(resp.data.message || 'API Error');
			} else {
				toast.success('Feature control saved successfully');
				localStorage.removeItem('authing');
				localStorage.removeItem('homePage');
				window.location.reload();
				refetch();
			}
		} catch (error) {
			toast.error(error?.response?.data?.message || error.message || 'Failed to save allocation');
		} finally {
			setLoading(false);
		}
	};

	const { tableItems, getIcon, requestSort } = useSortableData(
		MENU_ITEMS?.filter((item) => {
			let searchText = search?.toLowerCase();
			if (item?.title?.toLowerCase()?.includes(searchText) || item?.keyData?.toLowerCase()?.includes(searchText)) {
				if (filters?.classification === 'all') {
					return true;
				}
				return item.classification === filters?.classification;
			}
		}),
		'title',
		'asc',
	);

	return (
		<Main title='Feature Management' width={'100%'} height={'100%'}>
			<Toaster />
			<Header>
				<div className='flex justify-between items-center w-full'>
					<div className=''>
						<Input2 type={'text'} fieldName='Search' state={search} setState={setSearch} className='w-[300px] h-[40px] border-0' />
					</div>
					<div className='flex items-center'>
						<Button type='save' onClick={handleSave} className='text-red-500'>
							Save
						</Button>
						<Instruction keyId={'featureManagement'}/>
						<Button type='close' onClick={() => onClose()} className='text-red-500'>
							Close
						</Button>
					</div>
				</div>
			</Header>
			<Body className='w-full h-full'>
				{(isFetching || loading) && <OLoading styles={{ height: 60, width: 60 }} />}
				<Table width='100%' innerWidth={['10%', '40%', '30%', '20%']}>
					<Thead>
						<Tr>
							<Th position={0}>Si No</Th>
							<Th position={1} icon={getIcon} onClick={() => requestSort('title')}>
								Feature
							</Th>
							<Th position={2} classNameInset='p-0 m-0'>
								<div className='flex w-full p-0 m-0 h-full bg-transparent'>
									<select className='w-full p-0 m-0 h-full bg-transparent b-0 active:b-0' value={filters?.classification} onChange={(e) => setFilters((prev) => ({ ...prev, classification: e.target.value }))}>
										{classification &&
											Object.keys(classification).map((key, index) => (
												<option key={index} value={key}>
													{key}
												</option>
											))}
										<option value='all'>All</option>
									</select>
								</div>
							</Th>
							<Th position={3} onClick={() => requestSort('title')}>
								<div className='flex w-full justify-evenly items-center gap-2'>
									<input
										type='checkbox'
										className='w-4 h-4'
										checked={MENU_ITEMS.length == neededMenuItems?.length}
										onChange={() => {
											setLoading(true);
											setNeededMenuItems((prev) => {
												return prev?.length === MENU_ITEMS?.length ? [] : MENU_ITEMS.map((item) => item?.keyData);
											});
											setLoading(false);
										}}
									/>
									<span>Enabled</span>
								</div>
							</Th>
						</Tr>
					</Thead>
					<Tbody height='80vh'>
						{tableItems.map((item, index) => {
							return (
								<Tr key={index}>
									<Td position={0}>{index + 1}</Td>
									<Td position={1}>
										<div className='flex items-center'>
											<img src={icons[item.keyData] || item?.img} alt={item.title} className='w-9 h-9 mr-2' />
											<span className='text-lg font-semibold'>
												{item.title} - ({item?.keyData})
											</span>
										</div>
									</Td>
									<Td position={2}>{item.classification}</Td>
									<Td position={3}>
										<Switch
											checked={neededMenuItems?.includes(item?.keyData)}
											onChange={() => {
												setNeededMenuItems((prev) => {
													if (prev?.includes(item?.keyData)) {
														return prev.filter((i) => i !== item?.keyData);
													} else {
														return [...prev, item?.keyData];
													}
												});
											}}
										/>
									</Td>
								</Tr>
							);
						})}
					</Tbody>
				</Table>
			</Body>
		</Main>
	);
}

export default FeatureManagement;
